import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";

export interface MediaAssetRecord extends Record<string, unknown> {
  id: number;
  media_type: string;
  mime_type?: string | null;
  file_name?: string | null;
  width?: number | null;
  height?: number | null;
  duration_ms?: number | null;
  key?: string;
  url?: string;
  signed_url?: string;
  properties?: Record<string, unknown>;
}

interface MediaAssetPresignUpload {
  key: string;
  uploadUrl: string;
  method: "PUT";
  headers: Record<string, string>;
  mimetype: string;
  originalName: string;
  size: number;
  order: number;
  type: string;
}

interface MediaAssetPresignResponse {
  uploads: MediaAssetPresignUpload[];
  expiresIn: number;
}

interface CreateStandaloneMediaAssetInput {
  file: File;
  order: number;
}

export const mediaAssetsApi = {
  uploadStandalone: async (
    mediaInputs: CreateStandaloneMediaAssetInput[],
    options?: { audience?: string; properties?: Record<string, unknown> }
  ): Promise<MediaAssetRecord[]> => {
    if (mediaInputs.length === 0) {
      return [];
    }

    const presignResponse = await api.post<MediaAssetPresignResponse>(apiRoutes.mediaAssets.presign, {
      files: mediaInputs.map((entry) => ({
        name: entry.file.name,
        type: entry.file.type,
        size: entry.file.size,
        order: entry.order,
      })),
    });

    const uploads = Array.isArray(presignResponse.data?.uploads) ? presignResponse.data.uploads : [];
    if (uploads.length !== mediaInputs.length) {
      throw new Error("MEDIA_ASSET_UPLOAD_COUNT_MISMATCH");
    }

    for (let index = 0; index < uploads.length; index += 1) {
      const upload = uploads[index];
      const mediaInput = mediaInputs[index];
      const response = await fetch(upload.uploadUrl, {
        method: upload.method,
        headers: upload.headers,
        body: mediaInput.file,
      });
      if (!response.ok) {
        throw new Error(`MEDIA_ASSET_UPLOAD_FAILED:${response.status}`);
      }
    }

    const finalizeResponse = await api.post<{ items: MediaAssetRecord[] }>(apiRoutes.mediaAssets.finalize, {
      mediaFiles: uploads.map((upload) => ({
        key: upload.key,
        order: upload.order,
        originalName: upload.originalName,
        mimetype: upload.mimetype,
        size: upload.size,
        type: upload.type,
      })),
      audience: options?.audience ?? "public",
      properties: options?.properties,
    });

    return Array.isArray(finalizeResponse.data?.items) ? finalizeResponse.data.items : [];
  },
};
